import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQuery } from '../redux/api';
import { UsersStatisticListItem } from '../user';
import { PaginationMeta, VideoItem } from '../video';
import {
  GetOnlineMonthRequest,
  GetVideosViewsRequest,
  ViewedVideoItem,
  ViewsCount,
  WatchedVideoReportItem,
} from './types';

export const statisticsApi = createApi({
  baseQuery,
  endpoints: (builder) => ({
    top5Videos: builder.query<VideoItem[], { firmId?: number } | void>({
      transformResponse: (res: { data: VideoItem[] }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/video/statistic/most-viewed',
      }),
    }),
    getOnlineMonthByFirmId: builder.query<
      GetOnlineMonthRequest,
      { firmId?: number }
    >({
      transformResponse: (res: { data: GetOnlineMonthRequest }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/online/month',
      }),
    }),
    getVideoViews: builder.query<
      GetVideosViewsRequest[],
      { firmId?: number; type: string }
    >({
      transformResponse: (res: { data: GetVideosViewsRequest[] }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/video/views',
      }),
    }),
    getVideoViewsCount: builder.query<
      { items: ViewsCount[]; meta: PaginationMeta },
      { firmId?: number; page?: number; limit?: number }
    >({
      transformResponse: (res: {
        data: { items: ViewsCount[]; meta: PaginationMeta };
      }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/video/views/count',
      }),
    }),
    getUsersByFirmId: builder.query<
      { items: UsersStatisticListItem[]; meta: PaginationMeta },
      {
        firmId?: number;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      transformResponse: (res: {
        data: { items: UsersStatisticListItem[]; meta: PaginationMeta };
      }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/user',
      }),
    }),
    getVideosByUserId: builder.query<
      { items: ViewedVideoItem[]; meta: PaginationMeta },
      { id: number; page?: number; limit?: number }
    >({
      transformResponse: (res: {
        data: { items: ViewedVideoItem[]; meta: PaginationMeta };
      }) => res?.data,
      query: ({ id, page, limit }) => ({
        method: 'GET',
        params: { page, limit },
        url: `/admin/statistic/user/${id}/viewed`,
      }),
    }),
    getWatchedVideosReport: builder.query<
      { items: WatchedVideoReportItem[]; meta: PaginationMeta },
      { from?: string; to?: string; firmId?: number; page?: number; limit?: number; sortBy?: string; sortDir?: string }
    >({
      transformResponse: (res: { data: { items: WatchedVideoReportItem[]; meta: PaginationMeta } }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/watched-videos',
      }),
    }),
    getTotalVideosWatched: builder.query<
      { count: number },
      { from?: string; to?: string; firmId?: number }
    >({
      transformResponse: (res: { data: { count: number } }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/total-videos-watched',
      }),
    }),
    getActiveUsersCount: builder.query<
      { count: number },
      { from?: string; to?: string; firmId?: number }
    >({
      transformResponse: (res: { data: { count: number } }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/active-users-count',
      }),
    }),
    getUsersCount: builder.query<
      { count: number },
      { from?: string; to?: string; firmId?: number }
    >({
      transformResponse: (res: { data: { count: number } }) => res?.data,
      query: (params) => ({
        method: 'GET',
        params: params || undefined,
        url: '/admin/statistic/users-count',
      }),
    }),
    getWatchedVideosReportXLSX: builder.query<void, { firmId?: number; from?: string; to?: string; sortBy?: string; sortDir?: string }>({
      query: (params) => ({
        cache: 'no-cache',
        method: 'GET',
        params: params || undefined,
        responseHandler: async (response) =>
          window.location.assign(
            window.URL.createObjectURL(await response.blob()),
          ),
        url: '/admin/statistic/watched-videos-xlsx',
      }),
    }),
  }),
  reducerPath: 'statisticsApi',
});

export const {
  useGetActiveUsersCountQuery,
  useGetUsersCountQuery,
  useGetOnlineMonthByFirmIdQuery,
  useGetWatchedVideosReportXLSXQuery,
  useGetTotalVideosWatchedQuery,
  useGetVideoViewsQuery,
  useGetWatchedVideosReportQuery,
  useLazyGetWatchedVideosReportXLSXQuery,
  useLazyGetUsersByFirmIdQuery,
  useLazyGetVideoViewsCountQuery,
  useLazyGetVideosByUserIdQuery,
  useLazyTop5VideosQuery,
  useTop5VideosQuery,
} = statisticsApi;
